import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useAuthStore } from "@/store/authStore";
import { useBrandingStore } from "@/store/brandingStore";
import { brandingAssetUrl } from "@/api/branding";
import { getConfig } from "@/config";
import { InvestorPortalLink } from "@/components/auth/InvestorPortalLink";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

function LoginForm({ onRecaptcha }: { onRecaptcha?: () => Promise<string | undefined> }) {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const brand = useBrandingStore((s) => s.branding);
  const brandLogo = brandingAssetUrl(brand.logo_url);
  const [error, setError] = useState<string | null>(null);
  /**
   * A sign-in refused because this account belongs to another tenant
   * (MPPI-00820). Held separately from `error` because it is not a mistake
   * the person can fix by retyping: the password was right and the door was
   * wrong, so the useful response is a way through the right one, not a red
   * sentence above the same form.
   */
  const [wrongPortal, setWrongPortal] = useState<{
    message: string;
    url: string | null;
    host: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  /**
   * Where to go once signed in (MPPI-00828).
   *
   * `RequireAuth` and the API client both send people here with
   * `?returnUrl=<path>` when a session has expired or a deep link was
   * opened logged out. NOTHING read it: login navigated to "/"
   * unconditionally, so every such link silently lost its destination and
   * dropped the person on the dashboard. It matters more now that the
   * daily emails link straight into the portal — a recipient clicking a
   * property in the digest would have had to find it again by hand.
   *
   * **Only a same-site path is accepted.** A returnUrl is attacker-supplied
   * by construction (it is in a URL anyone can send), so taking it at face
   * value is an open redirect: "/login?returnUrl=https://evil.example" would
   * bounce an authenticated operator straight off the platform. A leading
   * "//" is rejected too, because "//evil.example" is protocol-relative and
   * a browser reads it as another ORIGIN, not as a path.
   */
  const returnTo = (() => {
    const raw = new URLSearchParams(location.search).get("returnUrl");
    if (!raw) return "/";
    let path: string;
    try {
      path = decodeURIComponent(raw);
    } catch {
      return "/";
    }
    if (!path.startsWith("/") || path.startsWith("//")) return "/";
    // Never bounce back to the sign-in flow itself — that is a loop.
    if (/^\/(login|mfa|set-new-password)\b/.test(path)) return "/";
    return path;
  })();

  /**
   * Why this person is looking at a sign-in page (MPPI-00876).
   *
   * Landing on a bare form after working in the admin reads as a fault, or
   * as the data having gone — which is exactly how 2026-10-20's "I see zero
   * in the data" began: an expired session left every panel empty with no
   * prompt and no explanation.
   *
   * Deliberately NOT styled as an error. Nothing went wrong with this
   * person or their password; a clock ran out. Same reasoning as the
   * wrong-portal panel below.
   */
  const expiryNotice = (() => {
    const reason = new URLSearchParams(location.search).get("reason");
    switch (reason) {
      case "expired":
        return "Your session timed out. Sign in again and we will take you back to where you were.";
      case "revoked":
        return "You were signed out because the session is no longer valid — a password change or a sign-out elsewhere will do that.";
      case "inactive":
        return "You were signed out after 24 hours without activity.";
      case "signed-out":
        return "You have been signed out.";
      default:
        return null;
    }
  })();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  if (isAuthenticated) {
    return null;
  }

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    setWrongPortal(null);
    setLoading(true);
    try {
      // Execute reCAPTCHA check before attempting login
      if (onRecaptcha) {
        await onRecaptcha();
      }
      const result = await login(data.email, data.password);
      // F-FORCE-PWD — Cognito returned NEW_PASSWORD_REQUIRED. Send the
      // user to the set-password screen; the store has already stashed
      // the Session token they need to complete the challenge.
      if ("requiresNewPassword" in result && result.requiresNewPassword) {
        navigate("/set-new-password");
      } else if ("requiresMfa" in result && result.requiresMfa) {
        // Carry the destination THROUGH the MFA step, or it is lost at the
        // one point in the flow most likely to be used.
        navigate(`/mfa?returnUrl=${encodeURIComponent(returnTo)}`);
      } else {
        navigate(returnTo);
      }
    } catch (err) {
      // The API answers a cross-tenant sign-in with code `wrong_portal` and
      // the address of the portal this account DOES belong to. Read from the
      // structured error rather than by matching words in the message, so
      // rewording the sentence server-side cannot silently turn this panel
      // back into a plain red box.
      const api = err as {
        code?: string;
        message?: string;
        detail?: { details?: { portal_url?: string | null; portal_host?: string | null } };
      };
      if (api?.code === "wrong_portal") {
        setWrongPortal({
          message: api.message ?? "This account cannot be used on this portal.",
          url: api.detail?.details?.portal_url ?? null,
          host: api.detail?.details?.portal_host ?? null,
        });
      } else {
        setError(err instanceof Error ? err.message : "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    // Content-height page (NOT viewport-height). Header + the two
    // brand/form panels + footer stack at their natural heights so
    // the footer appears RIGHT under the form rather than at the
    // bottom of a tall page with empty space between them.
    <div className="flex min-h-screen flex-col bg-white">
      {/* Top Header Bar — shorter than before so the form area can
          breathe in a single viewport. */}
      <header className="flex h-10 shrink-0 items-center border-b border-white/10 bg-primary px-4 lg:h-12 lg:px-8">
        {/* Logo, then the portal label, then the domain — the same order and
            left alignment as the investor portal's banner, so the two read as
            one family rather than two designs.

            The company NAME is not repeated beside the logo: the wordmark
            already carries it, and printing it twice in one eyeline is what
            the investor page had before it was removed. */}
        <a href="/" className="flex items-center gap-3">
          <img
            src={brandLogo ?? "/icon.png"}
            alt={brand.company_name}
            className="h-7 w-auto max-w-[180px] object-contain object-left"
          />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
            Administrator Portal
          </span>
          {brand.website ? (
            <span className="hidden text-xs text-white/70 sm:inline">
              {brand.website}
            </span>
          ) : null}
        </a>
      </header>

      <div className="flex flex-1">
      {/* Left Panel — Brand Showcase (hidden on mobile). Vertically
          centred — logo + bullets sit in the middle of the panel
          height. */}
      <div className="hidden bg-navy-gradient lg:flex lg:w-[60%] lg:flex-col lg:items-center lg:justify-center lg:px-10 lg:py-8">
        <img
          src={brandLogo ?? "/logo.png"}
          alt={brand.company_name}
          className="mb-4 h-28 w-auto object-contain"
        />

        <p className="mb-5 text-center font-display text-xl text-accent">
          Administration Portal
        </p>

        <ul className="max-w-md space-y-2 text-sm text-white">
          <li className="flex items-start gap-3">
            <span className="mt-1.5 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
            AI-driven analytics delivering real-time intelligence on the UAE property market
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-1.5 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
            Deep insights, trend detection, and signal analysis across every community and tower
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-1.5 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
            Lead generation, scoring, and outreach orchestration end-to-end
          </li>
        </ul>
      </div>

      {/* Right Panel — Login Form. Vertically centred so the form
          sits in the middle of the panel height between header and
          footer. */}
      <div className="flex flex-1 flex-col items-center justify-center bg-[#FAFAFA] px-6 py-8 lg:w-[40%]">
        <div className="w-full max-w-sm">
          <h1 className="text-center text-2xl font-semibold leading-tight text-gray-900">
            Admin Portal
          </h1>
          <p className="mt-1 text-center text-sm text-gray-500">
            Sign in to the {brand.company_name} administration console
          </p>

          {/* The reason comes first: it explains the page the person is
              looking at, before anything they might type on it. Suppressed
              once they have a real error, so a stale notice cannot sit
              above a live failure. */}
          {expiryNotice && !error && (
            <div
              className="mt-3 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700"
              role="status"
            >
              {expiryNotice}
            </div>
          )}

          {error && (
            <div
              className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}

          {wrongPortal && (
            // Deliberately NOT styled as an error. Nothing went wrong with
            // this person or their password; they followed a link to the
            // wrong building. Amber, a heading that says what happened, and
            // one button that finishes the job.
            <div
              className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
              role="alert"
            >
              <h2 className="text-sm font-semibold text-amber-900">
                You&rsquo;re on the wrong sign-in page
              </h2>
              <p className="mt-1 text-sm text-amber-800">{wrongPortal.message}</p>
              {wrongPortal.url ? (
                <a
                  href={wrongPortal.url}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-amber-900 px-3 py-2 text-sm font-medium text-white hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-700 focus:ring-offset-1"
                >
                  Go to {wrongPortal.host ?? "your portal"}
                  <span aria-hidden="true">&rarr;</span>
                </a>
              ) : (
                // No address to offer. Say that, rather than render a button
                // that goes nowhere — a dead control is worse than none.
                <p className="mt-2 text-xs text-amber-700">
                  Ask your administrator which address your organisation signs
                  in at.
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-3">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="relative mt-1">
                <svg
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className={`block w-full rounded-lg border bg-white py-2 pl-9 pr-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    errors.email
                      ? "border-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:border-primary"
                  }`}
                  {...register("email")}
                  aria-invalid={errors.email ? "true" : undefined}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
              </div>
              {errors.email && (
                <p id="email-error" className="mt-1 text-xs text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative mt-1">
                <svg
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className={`block w-full rounded-lg border bg-white py-2 pl-9 pr-9 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    errors.password
                      ? "border-red-500 focus:border-red-500"
                      : "border-neutral-300 focus:border-primary"
                  }`}
                  {...register("password")}
                  aria-invalid={errors.password ? "true" : undefined}
                  aria-describedby={errors.password ? "password-error" : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="mt-1 text-xs text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Sign In button — gold gradient */}
            <button
              type="submit"
              disabled={loading}
              className="flex h-10 w-full items-center justify-center rounded-lg bg-gold-gradient text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {loading ? (
                <svg className="h-5 w-5 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Contact + recaptcha — single tight block */}
          <InvestorPortalLink />

          <p className="mt-4 text-center text-xs text-gray-500">
            Need help? Contact your administrator
            {brand.support_phone ? (
              <>
                {" · "}
                <a
                  href={`tel:${brand.support_phone.replace(/[^+\d]/g, "")}`}
                  className="text-accent hover:underline"
                >
                  {brand.support_phone}
                </a>
              </>
            ) : null}
          </p>
          <p className="mt-2 text-center text-[11px] text-neutral-400">
            Protected by reCAPTCHA v3
          </p>
        </div>
      </div>
      </div>

      {/* Footer — navy bar so it reads as a real footer (the previous
          h-8 white line was easy to miss). Stays below the form panels
          via natural flow. */}
      <footer className="flex shrink-0 flex-col gap-1 bg-primary px-4 py-3 text-xs text-white sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-6 lg:px-10 lg:py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-semibold text-accent">{brand.company_name}</span>
          <span className="opacity-70">
            &copy; {new Date().getFullYear()}
            {brand.copyright_text ? ` ${brand.copyright_text}` : ""} — Admin Portal
          </span>
        </div>
        {/* Contact details come from the tenant's branding (MPPI-00263).
            These were hardcoded MPPI values, so a Gulf Token operator was
            given Mina Prime's website, phone and security address to contact.
            Each is omitted when the brand has not set it, rather than falling
            back to another company's. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] opacity-90">
          {brand.website ? <span>{brand.website}</span> : null}
          {brand.support_phone ? (
            <a href={`tel:${brand.support_phone.replace(/[^+\d]/g, "")}`} className="hover:text-accent">
              {brand.support_phone}
            </a>
          ) : null}
          {brand.support_email ? (
            <a href={`mailto:${brand.support_email}`} className="hover:text-accent">
              {brand.support_email}
            </a>
          ) : null}
        </div>
      </footer>
    </div>
  );
}

function RecaptchaLoginForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  return (
    <LoginForm
      onRecaptcha={executeRecaptcha ? () => executeRecaptcha("admin_login") : undefined}
    />
  );
}

export function LoginPage() {
  const recaptchaSiteKey = getConfig().recaptchaSiteKey ?? "";

  if (recaptchaSiteKey) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
        <RecaptchaLoginForm />
      </GoogleReCaptchaProvider>
    );
  }

  return <LoginForm />;
}
